import React, { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Icon } from '..';

type TabProps = {
  id: string;
  defaultActiveKey: string;
  className?: string;
  children:
    | React.ReactElement<TabContentProps>
    | React.ReactElement<TabContentProps>[];
  onTabChange?: (key: string) => void;
};

type TabContentProps = {
  eventKey: string;
  children: React.ReactNode;
  title: string;
  className: string;
};

export function TabContent({
  eventKey,
  title,
  children,
  className,
}: TabContentProps) {
  return null;
}

function Tab({
  id,
  defaultActiveKey,
  children,
  onTabChange,
  className,
}: TabProps) {
  const [activeKey, setActiveKey] = useState(defaultActiveKey);
  const [tabPressed, setTabPressed] = useState(!isMobile);

  let tabContents = [];

  if (Array.isArray(children)) {
    tabContents = children
      .filter(child => child?.type === TabContent)
      .map(child => child?.props);
  } else {
    tabContents = children ? [children.props] : [];
  }

  const handleTabChange = (key: string) => {
    setActiveKey(key);
    setTabPressed(isMobile ? !tabPressed : true);
    onTabChange?.(key);
  };

  const renderTabContent = (tab: TabContentProps) => {
    return (
      <div
        key={tab.eventKey}
        className={`h-full ${activeKey !== tab.eventKey ? 'hidden' : ''}`}
        id={tab.eventKey}
        role="tabpanel"
        aria-labelledby={`${tab.eventKey}Tab`}
      >
        {tab.children}
      </div>
    );
  };

  return (
    <>
      <ul
        className={`flex flex-row items-center flex-wrap p-2 ${className}`}
        role="tablist"
        id={id}
      >
        {tabContents.length > 0 &&
          tabContents.map(tab => (
            <li
              className="rounded-lg shadow-[0_0px_15px_0px_rgba(0,0,0,0.1)] bg-white w-full 
              tablet:shadow-none tablet:bg-transparent tablet:rounded-none tablet:w-fit first:pl-0 px-3"
              key={tab.eventKey}
            >
              <button
                id={`${tab.eventKey}Tab`}
                onClick={() => handleTabChange(tab.eventKey)}
                type="button"
                role="tab"
                aria-controls={tab.eventKey}
                aria-selected="false"
                className={`${
                  tab.className
                } min-w-[150px] py-2 text-center inline-flex items-center justify-center text-[#8f7319]
                hover:bg-pos-yellow rounded-md font-medium ${
                  activeKey === tab.eventKey &&
                  tabPressed &&
                  '!font-medium !text-pos-black bg-pos-yellow active'
                }`}
              >
                {tab.title}
                <Icon
                  id="tabDropdown"
                  className={`inline-block float-right tablet:hidden ${
                    activeKey === tab.eventKey &&
                    tabPressed &&
                    'rotate-180 tablet:rotate-0'
                  }`}
                />
              </button>
              <div
                id={`${id}Content`}
                className={`${!tabPressed && 'hidden'} flex-auto tablet:hidden`}
              >
                {renderTabContent(tab)}
              </div>
            </li>
          ))}
      </ul>

      <div id={`${id}Content`} className="hidden flex-auto tablet:block">
        {tabContents.length > 0 &&
          tabContents.map(tab => renderTabContent(tab))}
      </div>
    </>
  );
}

Tab.defaultProps = {
  className: '',
  onTabChange: undefined,
};

export default Tab;
